// Unpublished Work © 2026 Deere & Company.

import {queryOptions, useQuery} from '@tanstack/react-query';
import {
    checkSession,
    redirectToLoginPage,
    SESSION_USER_ID_STORAGE_KEY,
    SessionTransportError,
    shouldRedirectToLogin,
    type SessionData,
    UnauthenticatedSessionError,
} from '../../common/utils/sessions';

export const SESSION_QUERY_KEY = 'Session';

const TWELVE_HOURS_IN_MS = 12 * 60 * 60 * 1000;
const SESSION_RETRY_COUNT = 2;
const SESSION_RETRY_BASE_DELAY_IN_MS = 500;

export const sessionQueryOptions = queryOptions({
    queryKey: [SESSION_QUERY_KEY],
    queryFn: async (query) => {
        const previousSessionId = query.client.getQueryData<SessionData>([
            SESSION_QUERY_KEY,
        ])?.id;
        const session = await checkSession();

        if (previousSessionId && previousSessionId !== session.id) {
            throw new UnauthenticatedSessionError();
        }

        if (session.id) {
            window.localStorage.setItem(
                SESSION_USER_ID_STORAGE_KEY,
                session.id,
            );
        }

        return session;
    },
    meta: {
        onError: (error) => {
            if (shouldRedirectToLogin(error)) {
                redirectToLoginPage();
            }
        },
    },
    refetchOnWindowFocus: (query) => {
        const cachedSessionId = query.state.data?.id;
        const publishedSessionId = window.localStorage.getItem(
            SESSION_USER_ID_STORAGE_KEY,
        );

        if (cachedSessionId && cachedSessionId !== publishedSessionId) {
            redirectToLoginPage();
        }

        return 'always';
    },
    staleTime: TWELVE_HOURS_IN_MS,
    retry: (failureCount, error) =>
        error instanceof SessionTransportError &&
        failureCount < SESSION_RETRY_COUNT,
    retryDelay: (failureCount) =>
        SESSION_RETRY_BASE_DELAY_IN_MS * 3 ** failureCount,
});

export function useSession() {
    return useQuery(sessionQueryOptions);
}
